import { showError } from '@calendly/alert';
import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { DropdownMenuContent, DropdownMenuItem } from '@calendly/ui/components/dropdown-menu';
import { IconArrowRight } from '@calendly/ui/components/glyphs/IconArrowRight';
import { IconCircleX } from '@calendly/ui/components/glyphs/IconCircleX';
import { IconFlag } from '@calendly/ui/components/glyphs/IconFlag';
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash';
import { Loader } from '@calendly/ui/components/loader';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { zIndexDropdown } from '@calendly/ui/z-index';
import classNames from 'classnames';
import { useContext, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { useDestroyTrackedMeetingMutation } from 'hooks/tracked_meetings/api';
import { isCalendlyMeeting } from 'shared/utils';
import { isEventInProgress } from '../../../../shared/utils/get_badge_key';
import { MeetingDetailDependenciesContext } from '../meeting_detail_context';
const itemContentsCss = "i1r8mrcv";

/*
  CUI DropdownMenuContent on phone portals Menu via FixedContainer (default z-index 15).
  Meeting detail on small viewports lives in MobileSidePanelPortal at zIndexSidePanel (250),
  so the sheet would open under the panel and look like a dead ⋮. Elevate to zIndexDropdown
  (300): above the side panel, below zIndexModal (500) so action Popups/Modals stay on top.
*/
export const MORE_ACTIONS_MENU_STACKING_Z_INDEX = zIndexDropdown;
const moreActionsMenuStackingCss = "mwj2awv";
const ItemContents = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(itemContentsCss, props.className)} />;
});
const RemoveNotetakerItem = ({
  event,
  meetingIntelligence,
  isDropdownOpen,
  closeDropdown
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies) {
    throw new Error('RemoveNotetakerItem must be wrapped in a MeetingDetailDependenciesProvider');
  }
  const {
    transformLocationKind
  } = dependencies;
  const [removeTrackedMeeting, removeTrackedMeetingState] = useDestroyTrackedMeetingMutation();
  const track = useTrack();
  const dispatch = useDispatch();
  const onClickRemove = () => {
    if (removeTrackedMeetingState.isLoading) return;
    if (isEventInProgress(event)) {
      const locationKind = transformLocationKind(event.locationKind);
      dispatch(showError(I18n.t('publisher.meeting_intelligence.notetaker_toggle.cant_remove_notetaker', {
        locationKind
      })));
      return;
    }
    removeTrackedMeeting({
      meetingUuid: meetingIntelligence.trackedMeetingUuid,
      foreignEventId: event.uuid
    });
    track({
      name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
      properties: {
        action: 'disable_notetaker',
        location: 'meetings_page_right_rail',
        is_calendly_meeting: isCalendlyMeeting(event),
        event_uuid: event.uuid,
        mi_event_uuid: meetingIntelligence.trackedMeetingUuid
      }
    });
  };
  useOnValueChange(removeTrackedMeetingState?.isSuccess, () => {
    if (isDropdownOpen && removeTrackedMeetingState?.isSuccess) closeDropdown();
  });
  return <DropdownMenuItem onSelect={onClickRemove}>
      {removeTrackedMeetingState.isLoading && <Loader size="small" inline />}
      {!removeTrackedMeetingState.isLoading && <ItemContents>
          <UiIcon icon={IconCircleX} size="small" title="" />
          {I18n.t('publisher.meeting_intelligence.scheduled_event.remove')}
        </ItemContents>}
    </DropdownMenuItem>;
};

/** Rendered inside DropdownMenuContent; onClick is supplied by HostReschedule via rescheduleOnClickRef. */
export const RescheduleMenuItem = ({
  onClick
}) => <DropdownMenuItem onSelect={onClick}>
    <ItemContents>
      <UiIcon icon={IconArrowRight} size="small" title="" />
      {I18n.t('publisher.action.reschedule')}
    </ItemContents>
  </DropdownMenuItem>;
export const MoreActionsMenu = ({
  event,
  isDropdownOpen,
  meetingIntelligence,
  openCancelModal,
  openDeleteRecapModal,
  shouldShowCancelItem,
  shouldShowDeleteItem,
  shouldShowMenu,
  shouldShowRemoveNotetakerItem,
  shouldShowReportItem,
  showReportModal,
  closeDropdown,
  shouldShowRescheduleItem,
  rescheduleOnClickRef
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies) {
    throw new Error('MoreActionsMenu must be wrapped in a MeetingDetailDependenciesProvider');
  }
  const {
    getReportEventAnalyticsPayload
  } = dependencies;
  const track = useTrack();
  const sendAnalytics = () => {
    const payload = getReportEventAnalyticsPayload(event);
    track(payload);
  };
  const handleClickCancel = () => {
    closeDropdown();
    openCancelModal();
  };
  const handleClickDeleteRecap = () => {
    closeDropdown();
    openDeleteRecapModal();
  };
  const handleClickReport = () => {
    closeDropdown();
    sendAnalytics();
    showReportModal();
  };
  const handleClickReschedule = () => {
    closeDropdown();
    rescheduleOnClickRef?.current?.();
  };
  if (!shouldShowMenu) return null;
  return <DropdownMenuContent className={moreActionsMenuStackingCss} data-testid="more-actions-menu" placement="bottom end">
      {shouldShowRemoveNotetakerItem && <RemoveNotetakerItem event={event} meetingIntelligence={meetingIntelligence} isDropdownOpen={isDropdownOpen} closeDropdown={closeDropdown} />}
      {shouldShowRescheduleItem && <RescheduleMenuItem onClick={handleClickReschedule} />}
      {shouldShowCancelItem && <DropdownMenuItem onSelect={handleClickCancel}>
          <ItemContents>
            <UiIcon icon={IconTrash} size="small" title="" />
            {I18n.t('publisher.action.cancel')}
          </ItemContents>
        </DropdownMenuItem>}
      {shouldShowDeleteItem && <DropdownMenuItem onSelect={handleClickDeleteRecap}>
          <ItemContents>
            <UiIcon icon={IconTrash} size="small" title="" />
            {I18n.t('publisher.meeting_intelligence.details.header.delete')}
          </ItemContents>
        </DropdownMenuItem>}
      {shouldShowReportItem && <DropdownMenuItem onSelect={handleClickReport}>
          <ItemContents>
            <UiIcon icon={IconFlag} size="small" title="" />
            {I18n.t('publisher.meetings.report_event')}
          </ItemContents>
        </DropdownMenuItem>}
    </DropdownMenuContent>;
};

require("./more_actions_menu.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./more_actions_menu.js");